import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { CalibrationUsesLabelModel } from '../../models';
import { UsesService } from './index';

import { ToastrService } from 'ngx-toastr';
import { ZoningCodeService } from './zoning-code.service';

import * as _ from 'lodash';

import { AttributesStatisticCollectionNames } from '../../../shared/enums';
import { IZCEntitiesUpdatedData } from 'src/app/interfaces/zoning-code-model.interface';

@Injectable({
    providedIn: 'root'
})
export class ImportService {
    usesData$ = new BehaviorSubject<CalibrationUsesLabelModel[]>([]);

    constructor(
        private readonly usesService: UsesService,
        private readonly toastrService: ToastrService,
        private readonly zoningCodeService: ZoningCodeService
    ) {
        this.usesData$.subscribe(() => {
            this.validateUsesLabels();
        });
    }

    get areThereUsesLabels() {
        return Object.keys(this.usesData$.value).length;
    }

    private get usesTypes() {
        return this.usesData$.value;
    }

    private get zoningCode() {
        return this.zoningCodeService.selectedZoningCode;
    }

    importUses() {
        const preparedUses = this.getPreparedUses();
        this.usesService.addOrUpdateUsesLabels(preparedUses).subscribe(
            result => {
                this.toastrService.success('Uses are imported to Zoning Code', 'Success');

                const updatedData: IZCEntitiesUpdatedData = {
                    zoningCodeId: this.zoningCode.Id,
                    entityName: AttributesStatisticCollectionNames.CALIBRATION_USES_LABELS,
                    models: result.data
                };

                this.zoningCodeService.updateZoningCodeEntities(updatedData, false, false);
                this.usesData$.next([]);
            },
            () => this.toastrService.error('Error while uses importing to Zoning Code', 'Error')
        );
    }

    private getPreparedUses() {
        const usesLabels = Object.keys(this.usesTypes).reduce(
            (result, group) => result.concat(this.usesTypes[group]),
            []
        );
        return usesLabels.map(label => _.omit(label, 'importErrors'));
    }

    canImportUsesLabels() {
        const areThereErrors = Object.keys(this.usesTypes).some(useType => {
            return this.usesTypes[useType].some(currentUse => {
                return !!currentUse.importErrors;
            });
        });
        return !areThereErrors;
    }

    validateUsesLabels() {
        Object.keys(this.usesTypes).forEach(useType => {
            this.usesTypes[useType].forEach((currentUse, currentIndex) => {
                currentUse.importErrors = this.getUsesLabelErrors(currentUse.Label, currentIndex, useType);
            });
        });
    }

    getUsesLabelErrors(label: string, index?: number, useType?: string) {
        const duplicatesInImportData = this.checkDuplicatesInDataToImport(label, index, useType);
        const duplicatesInZCData = this.checkDuplicatesInsideZoningCodeData(label);
        return `${duplicatesInImportData}${duplicatesInZCData}`;
    }

    private checkDuplicatesInDataToImport(label: string, index?: number, useType?: string) {
        const usesTypesWithSameLabels = [];
        const labelToFind = label.toLowerCase().trim();

        Object.keys(this.usesTypes).forEach(currentUseType => {
            const found = this.usesTypes[currentUseType].find((currentLabel, currentIndex) => {
                return (
                    (currentUseType !== useType || currentIndex !== index) && // not the same value
                    currentLabel.Label.toLowerCase().trim() === labelToFind
                );
            });
            if (found) {
                usesTypesWithSameLabels.push(currentUseType);
            }
        });
        return usesTypesWithSameLabels.length
            ? `Exists in ${usesTypesWithSameLabels.join(', ')} uses on Import page.\n`
            : '';
    }

    private checkDuplicatesInsideZoningCodeData(label: string) {
        const usesTypesWithSameLabels = [];
        const labelToFind = label.toLowerCase().trim();

        this.zoningCode.CalibrationUsesTypes.forEach(currentUseType => {
            const found = currentUseType.labels.find(currentLabel => {
                return currentLabel.Label.toLowerCase().trim() === labelToFind;
            });
            if (found) {
                usesTypesWithSameLabels.push(currentUseType.Name);
            }
        });
        return usesTypesWithSameLabels.length
            ? `Exists in Zoning Code ${usesTypesWithSameLabels.join(', ')} uses. `
            : '';
    }
}
